<template>
    <div class="grid grid-cols-1" :class="[rows.length > 1 ? 'sm:grid-cols-2 gap-5' : 'sm:grid-cols-1 gap-5']">
        <div v-for="row in rows" :key="row.ticket.id || row.ticket.title" :class="[
            'group relative flex flex-col rounded-2xl border bg-card/70 backdrop-blur-2xl p-6 shadow-lg transition-all hover:-translate-y-1',
            row.ticket.featured ? 'border-brand-500/60 ring-1 ring-brand-500/30' : 'border-app'
        ]">
            <span v-if="row.ticket.featured"
                class="absolute -top-2.5 left-6 px-2.5 py-0.5 rounded-full bg-brand-500 text-white text-[10px] font-bold uppercase tracking-widest shadow">
                Most popular
            </span>

            <h3 class="text-header font-bold text-lg leading-snug">{{ row.ticket.title }}</h3>
            <p v-if="row.ticket.description" class="text-body/80 text-sm mt-1 line-clamp-2">{{ row.ticket.description }}</p>

            <div class="mt-4 mb-5 flex items-baseline gap-1">
                <span class="text-header font-black text-3xl">{{ priceLabel(row.ticket) }}</span>
                <span v-if="row.ticket.price > 0" class="text-body/80 text-xs font-bold uppercase tracking-wider">/ ticket</span>
            </div>

            <ul v-if="row.ticket.perks && row.ticket.perks.length" class="space-y-2 mb-6">
                <li v-for="(perk, p) in row.ticket.perks" :key="p" class="flex items-start gap-2 text-sm text-body">
                    <Check class="h-4 w-4 text-brand-500 shrink-0 mt-0.5" />
                    <span>{{ perk }}</span>
                </li>
            </ul>

            <div v-if="isSoldOut(row.ticket)"
                class="mt-auto w-full px-5 py-3 rounded-2xl bg-muted/10 text-body/80 text-center font-bold text-sm">
                Sold out
            </div>
            <div v-else class="mt-auto flex items-center justify-between rounded-2xl border border-app px-4 py-2.5">
                <span class="text-sm font-semibold text-header">Quantity</span>
                <div class="flex items-center gap-3">
                    <button type="button" @click="emit('update:qty', row.ticket, row.quantity - 1)" :disabled="row.quantity <= 0"
                        :aria-label="`Remove one ${row.ticket.title} ticket`"
                        class="h-8 w-8 rounded-lg border border-app text-header flex items-center justify-center hover:bg-muted/10 transition-colors disabled:opacity-40">
                        <Minus class="h-4 w-4" />
                    </button>
                    <span class="w-6 text-center font-bold text-header tabular-nums">{{ row.quantity }}</span>
                    <button type="button" @click="emit('update:qty', row.ticket, row.quantity + 1)" :disabled="row.quantity >= maxQty"
                        :aria-label="`Add one ${row.ticket.title} ticket`"
                        class="h-8 w-8 rounded-lg border border-app text-header flex items-center justify-center hover:bg-muted/10 transition-colors disabled:opacity-40">
                        <Plus class="h-4 w-4" />
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { Check, Plus, Minus } from 'lucide-vue-next';
import { EventTicketType } from '~/types';
import { formatMoney } from '../helpers';

defineProps<{
    rows: { ticket: EventTicketType; quantity: number }[];
    maxQty: number;
}>();

const emit = defineEmits<{ (e: 'update:qty', ticket: EventTicketType, quantity: number): void }>();

const isSoldOut = (t: EventTicketType) => t.soldOut === true || t.available === false;
const priceLabel = (t: EventTicketType) => formatMoney(t.price, t.currency);
</script>

<style scoped>
.bg-card {
    background-color: rgb(var(--core-card-bg));
}

.border-app {
    border-color: rgb(var(--core-app-border));
}

.text-header {
    color: rgb(var(--core-header-text));
}

.text-muted {
    color: rgb(var(--core-muted-text));
}

.text-body {
    color: rgb(var(--core-body-text));
}
</style>
